import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isNil } from 'lodash'

import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import Select from '@/components/forms/Select'
import { TextInput } from '@/components/forms/TextInput'
import { toast } from '@/components/toasts/toast.helpers'
import { createUsagePolicy, updateUsagePolicy } from '@/reducers/kodi/kodi.actions'

import './CreateUsagePolicyForm.scss'

const ORG_WIDE = 'org'
// One year — the longest rolling window the backend accepts.
const MAX_WINDOW_HOURS = 8760

// Common rolling windows, in hours, offered as one-click pills.
const WINDOW_PRESETS = [
  { label: '1 hour', hours: 1 },
  { label: '5 hours', hours: 5 },
  { label: '1 day', hours: 24 },
  { label: '7 days', hours: 168 },
]

/**
 * Create/edit modal body, shared by the "Create Usage Policy" button and each
 * row's edit pencil. In edit mode (`policy` set) the scope (user) and window are
 * locked — the backend only allows changing the token limit and enabled flag —
 * so only those two fields are editable.
 *
 * @param {number} props.organizationId - org the policy belongs to
 * @param {object} [props.policy] - existing row to edit; omit to create
 * @param {() => void} props.onClose - closes the modal (injected by ButtonAndFormModal)
 * @param {() => void} props.onSaved - called after a successful create/edit
 */
export const CreateUsagePolicyForm = ({ organizationId, policy, onClose, onSaved }) => {
  const dispatch = useDispatch()
  const isEdit = !isNil(policy)
  const users = useSelector((state) => state.organizationUsers.users)
  const [userValue, setUserValue] = useState(isEdit ? policy.user_id ?? ORG_WIDE : ORG_WIDE)
  const [windowHours, setWindowHours] = useState(isEdit ? String(policy.window_hours) : '')
  const [tokenLimit, setTokenLimit] = useState(isEdit ? String(policy.token_limit) : '')
  const [enabled, setEnabled] = useState(isEdit ? policy.enabled : true)
  const [submitting, setSubmitting] = useState(false)

  const userOptions = [
    { value: ORG_WIDE, label: 'All Users' },
    ...users.map((user) => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name}`,
    })),
  ]

  const parsedWindow = Number(windowHours)
  const parsedTokens = Number(tokenLimit)
  const canSubmit =
    windowHours !== '' &&
    tokenLimit !== '' &&
    Number.isInteger(parsedWindow) &&
    parsedWindow > 0 &&
    parsedWindow <= MAX_WINDOW_HOURS &&
    // Backend token_limit is an int >= 0, so reject fractional here too.
    Number.isInteger(parsedTokens) &&
    parsedTokens >= 0 &&
    !submitting

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      if (isEdit) {
        await dispatch(
          updateUsagePolicy({
            policyId: policy.id,
            organizationId,
            tokenLimit: parsedTokens,
            enabled,
          })
        )
        toast.success('Token usage policy updated')
      } else {
        await dispatch(
          createUsagePolicy({
            organizationId,
            userId: userValue === ORG_WIDE ? null : userValue,
            windowHours: parsedWindow,
            tokenLimit: parsedTokens,
            enabled,
          })
        )
        toast.success('Token usage policy created')
      }
      onSaved()
      onClose()
    } catch (err) {
      toast.error(err?.message || `Failed to ${isEdit ? 'update' : 'create'} usage policy`)
      setSubmitting(false)
    }
  }

  return (
    <div className="create-usage-policy-form">
      <label className="create-usage-policy-form__label" htmlFor="policy-token-limit">
        Token limit
      </label>
      <TextInput
        id="policy-token-limit"
        type="number"
        min={0}
        value={tokenLimit}
        onChange={(event) => setTokenLimit(event.target.value)}
        placeholder="e.g. 500000"
      />

      <label className="create-usage-policy-form__label" htmlFor="policy-window-hours">
        Window (hours)
      </label>
      <TextInput
        id="policy-window-hours"
        type="number"
        min={1}
        max={MAX_WINDOW_HOURS}
        value={windowHours}
        onChange={(event) => setWindowHours(event.target.value)}
        placeholder="e.g. 24"
        disabled={isEdit}
      />
      {!isEdit && (
        <div className="create-usage-policy-form__presets">
          {WINDOW_PRESETS.map((preset) => (
            <Pill
              key={preset.hours}
              clickable
              outlined
              selected={parsedWindow === preset.hours}
              onClick={() => setWindowHours(String(preset.hours))}
              dataTestId={`window-preset-${preset.hours}`}
            >
              {preset.label}
            </Pill>
          ))}
        </div>
      )}

      <label className="create-usage-policy-form__label" htmlFor="policy-user">
        User
      </label>
      <Select
        aria-label="User"
        inputId="policy-user"
        options={userOptions}
        value={userValue}
        onChange={({ value }) => setUserValue(value)}
        isClearable={false}
        isDisabled={isEdit}
        search
      />

      <div className="create-usage-policy-form__enabled">
        <span className="create-usage-policy-form__label">Status</span>
        <Pill
          clickable
          success={enabled}
          critical={!enabled}
          onClick={() => setEnabled((prev) => !prev)}
          dataTestId="policy-enabled-toggle"
        >
          {enabled ? 'Enabled' : 'Disabled'}
        </Pill>
      </div>

      <div className="form-actions is-modal">
        <Button type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button
          brand
          type="button"
          disabled={!canSubmit}
          disabledTooltip="Enter a token limit and window to continue"
          loading={submitting}
          onClick={handleSubmit}
          data-testid="usage-policy-submit"
        >
          {isEdit ? 'Save Changes' : 'Create Policy'}
        </Button>
      </div>
    </div>
  )
}
